'use client';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { useSession } from "@/hooks/useSessionHook";
import supabase from "@/supabase";
import TopHeader from '../components/TopHeader';
import BottomNav from '../components/BottomNav';
import MainSettings from '../components/settings/MainSettings';
import BillingSettings from '../components/settings/BillingSettings';
import CommunicationSettings from '../components/settings/CommunicationSettings';
import PrivacySettings from '../components/settings/PrivacySettings';
import NotificationSettings from '../components/settings/NotificationSettings';
import EditProfileSettingsWrapper from '../components/settings/EditProfileSettingsWrapper';
import AdminUserManagement from '../components/settings/AdminUserManagement';
import type { NotificationSetting } from '@/types/notification-settings';
import type { AdminRuntimeConfig } from '@/lib/admin-runtime-config';

interface SettingsViewState {
  currentView: string;
  history: string[];
}

interface CommunicationSetting {
  allowMessagesFriends: boolean;
  allowMessagesNonFriends: boolean;
  allowCalls: boolean;
}

interface PrivacySetting {
  findMeByPhone: boolean;
  findMeByEmail: boolean;
  autoLogoutMinutes: number | string;
  autoLogoutOnClose: boolean;
  requirePasskey: boolean;
  deleteAccount: boolean;
}

interface UserSettings {
  communication_setting: CommunicationSetting | null;
  privacy_setting: PrivacySetting | null;
  notification_settings: NotificationSetting | null;
  admin_settings: AdminRuntimeConfig | null;
}

interface SettingsData {
  communication?: Partial<CommunicationSetting>;
  privacy?: Partial<PrivacySetting>;
  notifications?: Partial<NotificationSetting>;
  admin?: Partial<AdminRuntimeConfig>;
}

export default function SettingsPage() {
  const [currentView, setCurrentView] = useState<SettingsViewState['currentView']>('main');
  const { session } = useSession();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [history, setHistory] = useState<SettingsViewState['history']>(['main']);

  useEffect(() => {
    const fetchSettingsData = async () => {
      if (!session?.user?.id) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('communication_setting, privacy_setting, notification_settings, admin_settings')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching settings data:', error);
        return;
      }

      setSettings(data as UserSettings | null);
    };

    fetchSettingsData();
  }, [session?.user?.id]);

  const handleNavigate = (view: string) => {
    setCurrentView(view);
    setHistory((prev) => [...prev, view]);
  };

  const handleBack = () => {
    if (history.length > 1) {
      const newHistory = history.slice(0, -1);
      const previousView = newHistory[newHistory.length - 1];
      setCurrentView(previousView as SettingsViewState['currentView']);
      setHistory(newHistory);
    }
  };

  const handleSaveSettings = async (data: Partial<SettingsData>) => {
    if (!session?.user?.id) return;

    try {
      console.log("Saving settings:", data);

      const nextSettings = {
        user_id: session.user.id,
        communication_setting: data.communication ?? settings?.communication_setting ?? null,
        privacy_setting: data.privacy ?? settings?.privacy_setting ?? null,
        notification_settings: data.notifications ?? settings?.notification_settings ?? null,
        admin_settings: data.admin ?? settings?.admin_settings ?? null,
      };

      const { data: savedSettings, error } = await supabase
        .from('user_settings')
        .upsert(nextSettings, { onConflict: 'user_id' })
        .select('communication_setting, privacy_setting, notification_settings, admin_settings')
        .single();

      if (error) {
        throw error;
      }

      setSettings(savedSettings as UserSettings);
      handleCancelSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      throw error;
    }
  };

  const handleCancelSettings = () => {
    handleNavigate('main');
  };

  const getHeaderTitle = () => {
    switch (currentView) {
      case 'main': return 'Settings';
      case 'billing': return 'Billing Settings';
      case 'communication': return 'Communication Settings';
      case 'privacy': return 'Privacy Settings';
      case 'notifications': return 'Notification Settings';
      case 'edit-profile': return 'Edit Profile';
      case 'manage-users': return 'Manage Users';
      default: return 'Settings';
    }
  };

  const renderSettingsView = () => {
    switch (currentView) {
      case 'main':
        return <MainSettings onNavigate={handleNavigate} />;
      case 'billing':
        return <BillingSettings onNavigate={handleNavigate} />;
      case 'communication':
        return <CommunicationSettings
          initialSettings={settings?.communication_setting || null}
          onSave={(settings: Partial<CommunicationSetting>) => handleSaveSettings({ communication: settings })}
          onCancel={handleCancelSettings}
        />;
      case 'privacy':
        return <PrivacySettings
          initialSettings={null}
          onSave={(settings: Partial<PrivacySetting>) => handleSaveSettings({ privacy: settings })}
          onCancel={handleCancelSettings}
        />;
      case 'notifications':
        return <NotificationSettings
          initialSettings={settings?.notification_settings || null}
          onSave={(settings: Partial<NotificationSetting>) => handleSaveSettings({ notifications: settings })}
        />;
      case 'edit-profile':
        return (
          <div className="jr-screen-pad py-5">
            <h1 className="jr-section-title mb-4 text-[2rem]">User Settings</h1>
            <EditProfileSettingsWrapper
              onSave={handleCancelSettings}
              onCancel={handleCancelSettings}
              setAvatarFile={() => {}}
            />
          </div>
        );
      case 'manage-users':
        return (
          <div className="jr-screen-pad py-5">
            <AdminUserManagement
              adminSettings={settings?.admin_settings || null}
              onSaveAdminSettings={(admin) => handleSaveSettings({ admin })}
            />
          </div>
        );
      default:
        return <MainSettings onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="jr-app-shell">
      <TopHeader />
      <header className="jr-subheader">
        {history.length > 1 && (
          <Button variant="ghost" size="icon" onClick={handleBack} className="mr-2 rounded-full text-[var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]">
            <ChevronLeft size={24} />
          </Button>
        )}
        <h2 className="jr-display text-2xl font-semibold">{getHeaderTitle()}</h2>
      </header>

      <main className="flex-1 overflow-y-auto pb-24">
        {renderSettingsView()}
      </main>

      <BottomNav />
    </div>
  );
}
